// Pequenos desenhos de terreno e objetos, compartilhados entre o mapa (decoração)
// e as cenas das cartas. Traço de tinta, cor chapada com sombra e luz.
// Cada função desenha em volta de (0,0), com a base do desenho em y = 0.

export const INK = '#1d1f30';

export const PAL = {
  pine: ['#3f6b4a', '#2c4f37', '#5b8a5f'],
  oak: ['#5f8a4c', '#456b39', '#86ad62'],
  trunk: '#6b4a32',
  rock: ['#9a968c', '#6f6c66', '#c4c0b4'],
  snow: ['#f4f6f8', '#c9d6e2', '#ffffff'],
  ice: ['#bfe0ef', '#8fbfd8', '#e6f6fd'],
  sand: ['#e3c27a', '#c69c4f', '#f3dda0'],
  grass: ['#9cb36b', '#7d9652', '#bfd18a'],
  crystal: ['#9c7cc8', '#6e4f9e', '#d5c2f0'],
  roof: ['#a3473a', '#7b3128'],
  wall: ['#e8dcc3', '#c2b292'],
  sea: ['#3f6e8c', '#2c5470'],
};

const sw = 1.3;

export function Pine({ s = 1, snow = false }: { s?: number; snow?: boolean }) {
  const [c, d, l] = PAL.pine;
  return (
    <g transform={`scale(${s})`}>
      <rect x={-1.2} y={-4} width={2.4} height={4} fill={PAL.trunk} stroke={INK} strokeWidth={sw * 0.8} />
      <path d="M0,-22 L7,-10 L4,-10 L9,-3 L-9,-3 L-4,-10 L-7,-10 Z" fill={c} stroke={INK} strokeWidth={sw} strokeLinejoin="round" />
      <path d="M0,-22 L7,-10 L4,-10 L9,-3 L1,-3 Z" fill={d} />
      <path d="M-1,-19 L-4,-12" stroke={l} strokeWidth={1.2} strokeLinecap="round" />
      {snow && <path d="M0,-22 L3.4,-16 C1.5,-15 -1.5,-15.5 -3.4,-16 Z M-4,-10 C-2,-9 2,-9.4 4,-10 L5.6,-8 C2,-7 -2,-7 -5.6,-8 Z" fill={PAL.snow[0]} />}
    </g>
  );
}

export function Oak({ s = 1 }: { s?: number }) {
  const [c, d, l] = PAL.oak;
  return (
    <g transform={`scale(${s})`}>
      <path d="M-1.6,0 L-1,-8 L1,-8 L1.6,0 Z" fill={PAL.trunk} stroke={INK} strokeWidth={sw * 0.8} />
      <path
        d="M0,-21 C5,-21 8,-17 7.4,-13.6 C10,-12 9.6,-7.2 6,-6.6 C4.4,-4.4 -4.4,-4.4 -6,-6.6 C-9.6,-7.2 -10,-12 -7.4,-13.6 C-8,-17 -5,-21 0,-21 Z"
        fill={c}
        stroke={INK}
        strokeWidth={sw}
      />
      <path d="M7.4,-13.6 C10,-12 9.6,-7.2 6,-6.6 C4.4,-4.4 -1,-4.6 -3,-5.6 C2,-7 5,-9 7.4,-13.6 Z" fill={d} />
      <circle cx={-3} cy={-15} r={2.2} fill={l} />
    </g>
  );
}

export function Palm({ s = 1 }: { s?: number }) {
  return (
    <g transform={`scale(${s})`}>
      <path d="M-0.5,0 C0,-6 1.6,-12 4,-17" fill="none" stroke={INK} strokeWidth={3.6} strokeLinecap="round" />
      <path d="M-0.5,0 C0,-6 1.6,-12 4,-17" fill="none" stroke="#9a6d43" strokeWidth={2} strokeLinecap="round" />
      {[
        'M4,-17 C0,-21 -6,-20 -9,-15 C-5,-17 0,-17 4,-17 Z',
        'M4,-17 C8,-22 14,-21 16,-16 C12,-18 8,-18 4,-17 Z',
        'M4,-17 C2,-24 6,-27 10,-26 C7,-24 5,-21 4,-17 Z',
        'M4,-17 C9,-16 12,-12 12,-8 C9,-12 7,-14 4,-17 Z',
        'M4,-17 C-1,-15 -3,-11 -3,-8 C0,-12 2,-14 4,-17 Z',
      ].map((d, i) => (
        <path key={i} d={d} fill={i % 2 ? '#4f8a4a' : '#67a35a'} stroke={INK} strokeWidth={sw * 0.9} strokeLinejoin="round" />
      ))}
    </g>
  );
}

export function Mountain({ s = 1, snow = false, tone = PAL.rock }: { s?: number; snow?: boolean; tone?: string[] }) {
  const [c, d, l] = tone;
  return (
    <g transform={`scale(${s})`}>
      <path d="M-16,0 L-4,-22 L1,-15 L5,-19 L16,0 Z" fill={c} stroke={INK} strokeWidth={sw} strokeLinejoin="round" />
      <path d="M-4,-22 L-1,-8 L4,0 L16,0 L5,-19 L1,-15 Z" fill={d} />
      <path d="M-4,-22 L-9,-9" stroke={l} strokeWidth={1.4} strokeLinecap="round" />
      {snow && (
        <path d="M-4,-22 L-8.4,-14 L-5.6,-15.6 L-3.6,-13.4 L-1.6,-16 L1,-15 Z M5,-19 L2.6,-14.6 L4.6,-15.6 L6.8,-14.2 Z" fill={PAL.snow[0]} stroke={INK} strokeWidth={0.8} strokeLinejoin="round" />
      )}
    </g>
  );
}

export function Hill({ s = 1, tone = PAL.grass }: { s?: number; tone?: string[] }) {
  const [c, d] = tone;
  return (
    <g transform={`scale(${s})`}>
      <path d="M-15,0 C-10,-9 -2,-11 4,-8 C9,-6 13,-3 15,0 Z" fill={c} stroke={INK} strokeWidth={sw} />
      <path d="M4,-8 C9,-6 13,-3 15,0 L2,0 C5,-3 5,-6 4,-8 Z" fill={d} />
    </g>
  );
}

export function Dune({ s = 1 }: { s?: number }) {
  const [c, d, l] = PAL.sand;
  return (
    <g transform={`scale(${s})`}>
      <path d="M-17,0 C-11,-8 -3,-11 3,-9 C8,-7 13,-3 17,0 Z" fill={c} stroke={INK} strokeWidth={sw} />
      <path d="M3,-9 C8,-7 13,-3 17,0 L-1,0 C3,-3 4,-6 3,-9 Z" fill={d} />
      <path d="M-10,-4 C-6,-7 -2,-7.6 1,-7" fill="none" stroke={l} strokeWidth={1.1} strokeLinecap="round" />
    </g>
  );
}

export function Pyramid({ s = 1 }: { s?: number }) {
  const [c, d] = PAL.sand;
  return (
    <g transform={`scale(${s})`}>
      <path d="M-14,0 L0,-18 L14,0 Z" fill={c} stroke={INK} strokeWidth={sw} strokeLinejoin="round" />
      <path d="M0,-18 L14,0 L3,0 Z" fill={d} />
      <path d="M-7,-7 H-1 M-10,-3.5 H-2" stroke={INK} strokeWidth={0.7} opacity={0.5} />
    </g>
  );
}

export function Crystal({ s = 1 }: { s?: number }) {
  const [c, d, l] = PAL.crystal;
  return (
    <g transform={`scale(${s})`}>
      <path d="M-8,0 L-9,-9 L-6,-14 L-3,-8 L-2,0 Z" fill={d} stroke={INK} strokeWidth={sw} strokeLinejoin="round" />
      <path d="M-3,0 L-4,-14 L0,-22 L4,-14 L3,0 Z" fill={c} stroke={INK} strokeWidth={sw} strokeLinejoin="round" />
      <path d="M0,-22 L4,-14 L3,0 L0.6,0 L1,-14 Z" fill={d} />
      <path d="M2,0 L3,-8 L7,-12 L9,-6 L8,0 Z" fill={c} stroke={INK} strokeWidth={sw} strokeLinejoin="round" />
      <path d="M-2,-15 L-2.6,-6" stroke={l} strokeWidth={1.2} strokeLinecap="round" />
    </g>
  );
}

export function IceSpire({ s = 1 }: { s?: number }) {
  const [c, d, l] = PAL.ice;
  return (
    <g transform={`scale(${s})`}>
      <path d="M-9,0 L-6,-12 L-3,-7 L0,-20 L4,-9 L6,-13 L9,0 Z" fill={c} stroke={INK} strokeWidth={sw} strokeLinejoin="round" />
      <path d="M0,-20 L4,-9 L6,-13 L9,0 L1,0 L2,-10 Z" fill={d} />
      <path d="M-1,-15 L-2.4,-6" stroke={l} strokeWidth={1.2} strokeLinecap="round" />
    </g>
  );
}

export function Reeds({ s = 1 }: { s?: number }) {
  return (
    <g transform={`scale(${s})`} fill="none" strokeLinecap="round">
      <path d="M-4,0 C-4,-5 -5,-8 -6,-11 M0,0 C0,-6 0.5,-10 1,-14 M4,0 C4,-4 5,-7 7,-9" stroke={INK} strokeWidth={2.2} />
      <path d="M-4,0 C-4,-5 -5,-8 -6,-11 M0,0 C0,-6 0.5,-10 1,-14 M4,0 C4,-4 5,-7 7,-9" stroke="#7d8f4e" strokeWidth={1} />
      <ellipse cx={1} cy={-12} rx={1.2} ry={2.4} fill="#7a5233" stroke={INK} strokeWidth={0.7} />
    </g>
  );
}

export function Castle({ s = 1 }: { s?: number }) {
  const [w, wd] = PAL.wall;
  const [r, rd] = PAL.roof;
  return (
    <g transform={`scale(${s})`} strokeLinejoin="round">
      <rect x={-10} y={-11} width={20} height={11} fill={w} stroke={INK} strokeWidth={sw} />
      <rect x={2} y={-11} width={8} height={11} fill={wd} />
      <rect x={-13} y={-18} width={7} height={18} fill={w} stroke={INK} strokeWidth={sw} />
      <rect x={6} y={-18} width={7} height={18} fill={wd} stroke={INK} strokeWidth={sw} />
      <path d="M-14,-18 L-9.5,-25 L-5,-18 Z" fill={r} stroke={INK} strokeWidth={sw} />
      <path d="M5,-18 L9.5,-25 L14,-18 Z" fill={rd} stroke={INK} strokeWidth={sw} />
      <path d="M-3,0 V-5 C-3,-7.6 3,-7.6 3,-5 V0" fill={INK} />
      <path d="M-9.5,-25 V-29 L-6.5,-28 L-9.5,-27" fill="#c9a23f" stroke={INK} strokeWidth={0.7} />
    </g>
  );
}

export function Ship({ s = 1, flip = false }: { s?: number; flip?: boolean }) {
  return (
    <g transform={`scale(${flip ? -s : s},${s})`} strokeLinejoin="round">
      <path d="M-12,-3 H12 L8,3 H-8 Z" fill="#7a5233" stroke={INK} strokeWidth={sw} />
      <path d="M0,-3 V-20" stroke={INK} strokeWidth={1.2} />
      <path d="M1,-19 C8,-16 9,-9 1,-5 Z" fill="#efe6d0" stroke={INK} strokeWidth={sw} />
      <path d="M-1,-17 C-6,-14 -6,-9 -1,-6 Z" fill="#e2d6bb" stroke={INK} strokeWidth={sw} />
      <path d="M0,-20 L4,-21.5 L0,-23" fill="#a23b3b" />
    </g>
  );
}

export function Wave({ s = 1 }: { s?: number }) {
  return (
    <path
      transform={`scale(${s})`}
      d="M-8,0 C-6,-3 -3,-3 -1,0 C1,-3 4,-3 6,0"
      fill="none"
      stroke="#2c5470"
      strokeWidth={1.1}
      strokeLinecap="round"
      opacity={0.45}
    />
  );
}

export function Volcano({ s = 1 }: { s?: number }) {
  return (
    <g transform={`scale(${s})`} strokeLinejoin="round">
      <path d="M-17,0 L-5,-19 L5,-19 L17,0 Z" fill="#6e5a4f" stroke={INK} strokeWidth={sw} />
      <path d="M2,-19 L5,-19 L17,0 L6,0 Z" fill="#4f3f37" />
      <path d="M-5,-19 C-3,-16 -1,-20 0,-17 C1,-20 3,-16 5,-19 Z" fill="#e2512d" stroke={INK} strokeWidth={0.9} />
      <path d="M-1,-17 C-2,-12 1,-10 -1,-4" fill="none" stroke="#e2512d" strokeWidth={1.6} strokeLinecap="round" />
      <path d="M-2,-22 C-6,-26 -2,-30 -5,-34 M2,-22 C5,-27 1,-30 4,-35" fill="none" stroke="#8f8a86" strokeWidth={2.4} strokeLinecap="round" opacity={0.6} />
    </g>
  );
}

export function Mushroom({ s = 1 }: { s?: number }) {
  return (
    <g transform={`scale(${s})`}>
      <path d="M-1.6,0 L-1.2,-6 H1.2 L1.6,0 Z" fill="#efe6d0" stroke={INK} strokeWidth={sw * 0.8} />
      <path d="M-7,-5 C-7,-12 7,-12 7,-5 Z" fill="#8e5bb8" stroke={INK} strokeWidth={sw} />
      <circle cx={-2.6} cy={-8} r={1.1} fill="#e8d8f6" />
      <circle cx={2.6} cy={-7} r={0.9} fill="#e8d8f6" />
    </g>
  );
}
